import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ArrowLeft, Loader2, Users, Hash, BellOff, Bell, Search, X } from 'lucide-react'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { ReconnectingBanner } from './ReconnectingBanner'
import { OnlineIndicator } from './OnlineIndicator'
import { GrupoMiembrosDialog } from './GrupoMiembrosDialog'
import { useMensajes, useEnviarMensaje, useToggleMute, useMarcarLeido, useReadReceipts, useBuscarMensajes, useSubirArchivo } from '@/core/hooks/useChat'
import { useChatContext } from '@/core/providers/ChatProvider'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'
import type { ConversacionConPreview } from '@/core/types/chat'
import { CHAT_FILE_MAX_SIZE } from '@/core/types/chat'
import { toast } from 'sonner'

interface ChatWindowProps {
  conversacion: ConversacionConPreview
  onBack: () => void
}

export function ChatWindow({ conversacion, onBack }: ChatWindowProps) {
  const { user } = useAuth0()
  const { connectionStatus, isOnline, sendBroadcast, sendTyping, getTypingUsers, sendReadReceipt, getReadBy } = useChatContext()
  const { data: mensajes = [], isLoading } = useMensajes(conversacion.id)
  const enviarMensaje = useEnviarMensaje()
  const subirArchivo = useSubirArchivo()
  const toggleMute = useToggleMute()
  const marcarLeido = useMarcarLeido()
  const { data: initialReadReceipts } = useReadReceipts(conversacion.id)
  const [isMuted, setIsMuted] = useState(conversacion.muted)
  const [miembrosOpen, setMiembrosOpen] = useState(false)
  const [searchMode, setSearchMode] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const lastMarkedRef = useRef<string | null>(null)
  const endOfMessagesRef = useRef<HTMLDivElement>(null)
  const prevCountRef = useRef(0)
  const resolveNombre = useUserNameResolver()

  const myEmail = user?.email ?? ''
  const isDirect = conversacion.tipo === 'direct'
  const isChannel = conversacion.tipo === 'channel'

  // Display name and icon based on type
  const displayName = isDirect
    ? resolveNombre(conversacion.otro_usuario)
    : (conversacion.nombre ?? conversacion.otro_usuario)

  const typingEmails = getTypingUsers(conversacion.id)

  const handleTyping = useCallback((isTyping: boolean) => {
    sendTyping(conversacion.id, isTyping)
  }, [sendTyping, conversacion.id])

  const displaySubtitle = isDirect
    ? (isOnline(conversacion.otro_usuario) ? 'En línea' : 'Desconectado')
    : `${conversacion.member_count ?? 0} miembros`

  // Auto-mark conversation as read when the latest NON-OWN message changes.
  // Bug fix: el guard anterior "sender_email === myEmail" abortaba el efecto
  // cuando el último mensaje era tuyo (ej. acabás de responder), dejando los
  // mensajes previos del otro como no leídos eternamente. Ahora buscamos el
  // último mensaje no propio con .find() sobre el array DESC.
  useEffect(() => {
    if (!mensajes.length) return
    const latestOtherMessage = mensajes.find(
      (m) => !m.id.startsWith('temp-') && m.sender_email !== myEmail
    )
    if (!latestOtherMessage) return
    if (latestOtherMessage.id === lastMarkedRef.current) return

    lastMarkedRef.current = latestOtherMessage.id
    marcarLeido.mutate({ conversacionId: conversacion.id, messageId: latestOtherMessage.id })
    sendReadReceipt(conversacion.id, latestOtherMessage.id)
  }, [mensajes, conversacion.id, myEmail, marcarLeido, sendReadReceipt])

  // Combine DB read receipts with broadcast read receipts for a message
  const getReadByForMessage = useCallback((messageId: string): string[] => {
    const broadcastReaders = getReadBy(conversacion.id, messageId)
    if (!initialReadReceipts) return broadcastReaders

    const dbReaders = initialReadReceipts
      .filter((r) => r.last_read_message_id && r.last_read_message_id >= messageId && r.user_email !== myEmail)
      .map((r) => r.user_email)

    return [...new Set([...dbReaders, ...broadcastReaders])]
  }, [initialReadReceipts, getReadBy, conversacion.id, myEmail])

  // M6: scroll al fondo usando un anchor <div> + scrollIntoView.
  // Más robusto que consultar [data-radix-scroll-area-viewport] (API interna de Radix).
  useEffect(() => {
    if (mensajes.length > prevCountRef.current) {
      endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
    prevCountRef.current = mensajes.length
  }, [mensajes.length])

  // H1 fix: broadcast con el mensaje REAL (ID de BD), no un ID fake.
  // Antes se disparaba broadcast inmediato con `broadcast-${Date.now()}` que
  // no matcheaba con el ID real, causando duplicados cross-client.
  const handleSend = (content: string) => {
    enviarMensaje.mutate(
      { conversacionId: conversacion.id, content },
      {
        onSuccess: (realMessage) => {
          sendBroadcast(realMessage)
        },
      }
    )
  }

  const handleAttachment = (file: File) => {
    if (file.size > CHAT_FILE_MAX_SIZE) {
      toast.error('El archivo excede 10MB')
      return
    }
    subirArchivo.mutate(
      { file, conversacionId: conversacion.id },
      {
        onSuccess: (msg) => {
          sendBroadcast(msg)
        },
      }
    )
  }

  const { data: searchResults } = useBuscarMensajes(searchQuery, conversacion.id)

  const displayMessages = useMemo(() => {
    const source = searchMode && searchResults ? searchResults : mensajes
    return [...source].reverse()
  }, [mensajes, searchMode, searchResults])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-background shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="relative">
          {isDirect ? (
            <>
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                {displayName[0]?.toUpperCase()}
              </div>
              <OnlineIndicator online={isOnline(conversacion.otro_usuario)} className="absolute -bottom-0.5 -right-0.5" />
            </>
          ) : (
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${isChannel ? 'bg-blue-500/10 text-blue-600' : 'bg-violet-500/10 text-violet-600'}`}>
              {isChannel ? <Hash className="h-4 w-4" /> : <Users className="h-4 w-4" />}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{displayName}</p>
          <p className="text-xs text-muted-foreground">{displaySubtitle}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => { setSearchMode(!searchMode); setSearchQuery('') }}
          title="Buscar"
        >
          <Search className="h-4 w-4" />
        </Button>
        {!isDirect && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setMiembrosOpen(true)}
            title="Miembros"
          >
            <Users className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => {
            toggleMute.mutate(conversacion.id, {
              onSuccess: (muted) => setIsMuted(muted),
            })
          }}
          title={isMuted ? 'Activar notificaciones' : 'Silenciar'}
        >
          {isMuted ? <BellOff className="h-4 w-4 text-muted-foreground" /> : <Bell className="h-4 w-4" />}
        </Button>
      </div>

      {!isDirect && (
        <GrupoMiembrosDialog
          conversacionId={conversacion.id}
          open={miembrosOpen}
          onOpenChange={setMiembrosOpen}
          isAdmin={conversacion.created_by === myEmail}
        />
      )}

      {searchMode && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-muted/30 shrink-0">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar en mensajes..."
            className="h-7 text-sm border-0 bg-transparent focus-visible:ring-0 px-0"
            autoFocus
          />
          {searchQuery && (
            <span className="text-[10px] text-muted-foreground shrink-0">
              {searchResults?.length ?? 0} resultado{(searchResults?.length ?? 0) !== 1 ? 's' : ''}
            </span>
          )}
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => { setSearchMode(false); setSearchQuery('') }}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      <ReconnectingBanner status={connectionStatus} />

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : displayMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-muted-foreground">
                {isDirect ? `Iniciá la conversación con ${displayName}` : `Enviá el primer mensaje en ${displayName}`}
              </p>
            </div>
          ) : (
            displayMessages.map((msg) => (
              <ChatMessage
                key={msg.id}
                mensaje={msg}
                isMine={msg.sender_email === myEmail}
                showSender={!isDirect}
                senderName={resolveNombre(msg.sender_email)}
                readBy={msg.sender_email === myEmail ? getReadByForMessage(msg.id) : undefined}
                totalMembers={!isDirect ? (conversacion.member_count ?? 0) : undefined}
                isDirect={isDirect}
                highlightQuery={searchMode ? searchQuery : undefined}
              />
            ))
          )}
          <div ref={endOfMessagesRef} />
        </div>
      </ScrollArea>

      {typingEmails.length > 0 && (
        <div className="px-3 py-1 text-xs text-muted-foreground animate-pulse">
          {typingEmails.length === 1
            ? `${resolveNombre(typingEmails[0])} está escribiendo...`
            : typingEmails.length === 2
              ? `${resolveNombre(typingEmails[0])} y ${resolveNombre(typingEmails[1])} están escribiendo...`
              : `${typingEmails.length} personas están escribiendo...`
          }
        </div>
      )}

      <ChatInput onSend={handleSend} onTyping={handleTyping} onAttachment={handleAttachment} disabled={connectionStatus === 'disconnected'} />
    </div>
  )
}
